/*
 * promo-equal-earth.js
 * Cartovia — encart « Le monde à sa vraie taille » de la page d'accueil (v1.0, 2026-10-16)
 *
 * Fait passer une carte du monde de la projection de Mercator à la projection Equal Earth.
 * Vanilla JS, aucune librairie. Chargé en `defer` par sections/promo-equal-earth.liquid.
 *
 * Markup attendu (voir maquette_encart-equal-earth_homepage.html) :
 *   [data-ee-morph]                racine (la <figure>) ; data-ee-src = URL du JSON,
 *                                  data-ee-animate="false" pour couper l'animation automatique
 *   [data-ee-frame]                cadre 3:2 observé pour lancer l'animation
 *   img[data-ee-fallback]          carte Equal Earth statique (sans JS, ou si le JSON ne charge pas)
 *   svg[data-ee-svg][hidden]       carte animée, avec 5 <path data-ee-layer="grat|eq|land|africa|greenland">
 *   [data-ee-toggle][hidden]       les deux boutons, chacun avec data-ee-to="0" (Mercator) ou "1" (Equal Earth)
 *   [data-ee-ratio]                rapport des surfaces Afrique / Groenland à l'écran, mis à jour en direct
 *
 * Données (assets/equal-earth-morph.json) : { s, w, h, land, africa, greenland, grat, eq }
 *   chaque anneau = tableau d'entiers [xMercator, yMercator, xEqualEarth, yEqualEarth, ...] en 1/s px
 *   d'une viewBox w x h ; la carte à l'instant t (0 = Mercator, 1 = Equal Earth) est l'interpolation linéaire.
 */
(function () {
  'use strict';

  var DURATION = 1700; // ms pour un trajet complet Mercator -> Equal Earth
  var AUTOPLAY_DELAY = 600; // ms après que le cadre est visible à 40 %
  var LAYERS = ['grat', 'eq', 'land', 'africa', 'greenland'];
  var CLOSED = { land: true, africa: true, greenland: true };

  function easeInOutCubic(x) {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  }

  function buildPath(rings, t, s, close) {
    var out = '';
    for (var r = 0; r < rings.length; r++) {
      var ring = rings[r];
      for (var i = 0; i < ring.length; i += 4) {
        var x = (ring[i] + (ring[i + 2] - ring[i]) * t) / s;
        var y = (ring[i + 1] + (ring[i + 3] - ring[i + 1]) * t) / s;
        out += (i ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1);
      }
      if (close) out += 'Z';
    }
    return out;
  }

  // Aire (formule du lacet) des anneaux à l'instant t ; l'unité n'importe pas, seul le rapport sert.
  function area(rings, t) {
    var total = 0;
    for (var r = 0; r < rings.length; r++) {
      var ring = rings[r];
      var n = ring.length / 4;
      var sum = 0;
      for (var k = 0; k < n; k++) {
        var i = k * 4;
        var j = ((k + 1) % n) * 4;
        var x1 = ring[i] + (ring[i + 2] - ring[i]) * t;
        var y1 = ring[i + 1] + (ring[i + 3] - ring[i + 1]) * t;
        var x2 = ring[j] + (ring[j + 2] - ring[j]) * t;
        var y2 = ring[j + 1] + (ring[j + 3] - ring[j + 1]) * t;
        sum += x1 * y2 - x2 * y1;
      }
      total += Math.abs(sum) / 2;
    }
    return total;
  }

  function EqualEarthMorph(root) {
    var self = this;
    this.root = root;
    this.frame = root.querySelector('[data-ee-frame]') || root;
    this.svg = root.querySelector('[data-ee-svg]');
    this.fallback = root.querySelector('[data-ee-fallback]');
    this.toggle = root.querySelector('[data-ee-toggle]');
    this.buttons = Array.prototype.slice.call(root.querySelectorAll('[data-ee-to]'));
    this.ratioEl = root.querySelector('[data-ee-ratio]');
    this.layers = {};
    LAYERS.forEach(function (name) {
      self.layers[name] = self.svg ? self.svg.querySelector('[data-ee-layer="' + name + '"]') : null;
    });
    if (!this.svg || !this.layers.land) return; // markup incomplet : on garde l'image statique

    this.reduced =
      (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
      root.getAttribute('data-ee-animate') === 'false';
    var lang = (document.documentElement.getAttribute('lang') || 'fr').slice(0, 2);
    try {
      this.fmt = new Intl.NumberFormat(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    } catch (e) {
      this.fmt = new Intl.NumberFormat('fr', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }

    this.data = null;
    this.t = 1;
    this.target = 1;
    this.raf = 0;
    this.timer = 0;
    this.played = false;

    this.buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        self.played = true;
        self.go(Number(button.getAttribute('data-ee-to')));
      });
    });
    this.whenNear();
  }

  // Ne télécharge le JSON que lorsque l'encart approche de l'écran.
  EqualEarthMorph.prototype.whenNear = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }
    var near = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            near.disconnect();
            self.load();
            return;
          }
        }
      },
      { rootMargin: '300px 0px' }
    );
    near.observe(this.root);
  };

  EqualEarthMorph.prototype.load = function () {
    var self = this;
    var src = this.root.getAttribute('data-ee-src');
    var inline = this.root.querySelector('script[data-ee-data]');
    var request = src
      ? fetch(src, { credentials: 'same-origin' }).then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
      : Promise.resolve(inline ? JSON.parse(inline.textContent) : null);
    request
      .then(function (data) {
        if (data && data.land) self.ready(data);
      })
      .catch(function () {
        /* l'image statique reste affichée */
      });
  };

  EqualEarthMorph.prototype.ready = function (data) {
    this.data = data;
    // Le visiteur n'a pas encore vu la carte animée : on part de Mercator, sauf si le mouvement est réduit.
    this.t = this.target = this.reduced ? 1 : 0;
    this.draw();
    this.svg.removeAttribute('hidden');
    if (this.fallback) this.fallback.setAttribute('hidden', '');
    if (this.toggle) this.toggle.removeAttribute('hidden');
    this.setPressed();
    if (!this.reduced) this.whenVisible();
  };

  // Joue la transformation une fois, quand le cadre est visible à 40 %.
  EqualEarthMorph.prototype.whenVisible = function () {
    var self = this;
    var start = function () {
      if (self.played) return;
      self.played = true;
      self.timer = setTimeout(function () {
        self.go(1);
      }, AUTOPLAY_DELAY);
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    var seen = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            seen.disconnect();
            start();
            return;
          }
        }
      },
      { threshold: 0.4 }
    );
    seen.observe(this.frame);
  };

  EqualEarthMorph.prototype.go = function (target) {
    var self = this;
    clearTimeout(this.timer);
    cancelAnimationFrame(this.raf);
    this.target = target;
    this.setPressed();
    if (!this.data) return;
    var from = this.t;
    if (this.reduced || from === target) {
      this.t = target;
      this.draw();
      return;
    }
    var duration = DURATION * Math.abs(target - from);
    var startTime = null;
    var step = function (now) {
      if (startTime === null) startTime = now;
      var p = Math.min(1, (now - startTime) / duration);
      self.t = from + (target - from) * easeInOutCubic(p);
      self.draw();
      if (p < 1) self.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  };

  EqualEarthMorph.prototype.setPressed = function () {
    var target = this.target;
    this.buttons.forEach(function (button) {
      button.setAttribute('aria-pressed', String(Number(button.getAttribute('data-ee-to')) === target));
    });
  };

  EqualEarthMorph.prototype.draw = function () {
    var d = this.data;
    var s = d.s || 1;
    for (var i = 0; i < LAYERS.length; i++) {
      var name = LAYERS[i];
      if (this.layers[name] && d[name]) {
        this.layers[name].setAttribute('d', buildPath(d[name], this.t, s, !!CLOSED[name]));
      }
    }
    if (this.ratioEl) {
      var g = area(d.greenland, this.t);
      if (g > 0) this.ratioEl.textContent = this.fmt.format(area(d.africa, this.t) / g);
    }
  };

  EqualEarthMorph.prototype.destroy = function () {
    clearTimeout(this.timer);
    cancelAnimationFrame(this.raf);
  };

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-ee-morph]');
    Array.prototype.forEach.call(nodes, function (node) {
      if (!node.__eeMorph) node.__eeMorph = new EqualEarthMorph(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Éditeur de thème : réinitialiser quand la section est ajoutée ou rechargée.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var nodes = event.target.querySelectorAll('[data-ee-morph]');
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.__eeMorph && node.__eeMorph.destroy) node.__eeMorph.destroy();
    });
  });
})();
